<script setup lang="ts">
/**
 * The right-hand column: people, and only people.
 *
 * Shows who is waiting on an answer, who you are connected to, and who you
 * might want to meet — and lets you open a chat in one click, which is the
 * shortest path from "home" to "talking to someone".
 *
 * The **Trending topics** panel from the design sits at the top, built to its
 * shape and waiting for a system to feed it — see `TrendingPanel.vue`.
 *
 * "People you may want to meet" replaces the design's "who to follow". It is
 * the same idea against real data: `/api/discover`, whose ranking is
 * documented and explicit. Their "12 mutual friends" subtitle is not here —
 * nothing computes a mutual count — so each row carries where they are and
 * what they read, which is what this product actually matches on.
 */
import { computed, onMounted, ref, watch } from 'vue';
import { RouterLink } from 'vue-router';

import Avatar from './Avatar.vue';
import NotificationBadge from './NotificationBadge.vue';
import Skeleton from './Skeleton.vue';
import SkeletonRow from './SkeletonRow.vue';
import TrendingPanel from './TrendingPanel.vue';
import { countryName, languageName } from '../services/translation.ts';
import { usePresenceStore } from '../stores/presence.ts';
import { useRealtimeStore } from '../stores/realtime.ts';
import { useUserStore } from '../stores/user.ts';
import { useStartConversation } from '../composables/useStartConversation.ts';
import { countryFlag } from '../utils/format.ts';
import { intlLocale, useI18n } from '../i18n/index.ts';

const users = useUserStore();
const { t } = useI18n();
const presence = usePresenceStore();
const realtime = useRealtimeStore();
const { openWith, busyUserId } = useStartConversation();

const friends = computed(() => users.friends.slice(0, 8));
const pending = computed(() => users.incomingRequests.slice(0, 3));
const suggestions = computed(() => users.discovered.slice(0, 3));

const connecting = ref<string | null>(null);

async function connect(userId: string): Promise<void> {
  if (connecting.value) return;
  connecting.value = userId;
  try {
    await users.connect(userId);
  } catch {
    // The row stays as it was; the store still matches the server.
  } finally {
    connecting.value = null;
  }
}

/**
 * The request currently being answered, so its two buttons disable together
 * and a double-tap cannot send accept and decline for the same row.
 */
const answering = ref<string | null>(null);

async function answer(requestId: string, accept: boolean): Promise<void> {
  if (answering.value) return;
  answering.value = requestId;
  try {
    await (accept ? users.acceptRequest(requestId) : users.rejectRequest(requestId));
  } catch {
    // The row stays; the list still matches the server.
  } finally {
    answering.value = null;
  }
}

/**
 * Both lists below are fetched here, so before they land the rail has nothing
 * to show. Rendering the real markup straight away meant the empty state
 * flashed and the requests section appeared late, shoving Contacts down the
 * moment the data arrived. Hold a skeleton of the same shape until both
 * settle, and the rail is laid out once.
 */
const ready = ref(false);

/**
 * Subscribe to presence for whoever is on screen, and only for them.
 *
 * Watching every friend an account has would put an unbounded list on the
 * wire for a column that shows twelve of them.
 */
watch(
  friends,
  (list) => {
    realtime.watch(list.map((friend) => friend.id));
  },
  { immediate: true },
);

onMounted(() => {
  void Promise.all([
    users.friends.length === 0 ? users.loadFriends() : undefined,
    users.incomingRequests.length === 0 ? users.loadRequests() : undefined,
    users.discovered.length === 0 ? users.search({}) : undefined,
  ]).finally(() => {
    ready.value = true;
  });
});
</script>

<template>
  <aside class="flex flex-col gap-space-lg py-space-lg" :aria-label="t('nav.contacts')">
    <TrendingPanel />

    <section
      v-if="!ready"
      class="rounded-xl bg-surface-container-lowest p-space-md shadow-sm"
      aria-busy="true"
      :aria-label="t('common.loading')"
    >
      <Skeleton class="mb-3 h-4 w-24" />
      <SkeletonRow v-for="n in 5" :key="n" />
    </section>

    <!-- Requests first: the only thing here that needs an answer, and it can
         be given here. Sending somebody to another page to press Accept is a
         detour for a decision they have already made. -->
    <section
      v-if="ready && pending.length"
      class="rounded-xl bg-surface-container-lowest p-space-md shadow-sm"
    >
      <div class="mb-3 flex items-center justify-between gap-2">
        <h2 class="text-headline-sm text-on-surface">{{ t('nav.connectionRequests') }}</h2>
        <NotificationBadge :count="users.incomingRequests.length" />
      </div>

      <div v-for="request in pending" :key="request.id" class="mb-2 last:mb-0">
        <RouterLink :to="`/@${request.user.username}`" class="flex items-start gap-2.5">
          <Avatar
            :name="request.user.displayName"
            :src="request.user.avatarUrl"
            :asset="request.user.avatarAsset"
            :user-id="request.user.id"
            size="sm"
          />
          <span class="min-w-0 flex-1 text-body-sm leading-snug">
            <span class="text-label-md text-on-surface">{{ request.user.displayName }}</span>
            <span class="text-on-surface-variant"> {{ t('nav.wantsToConnect') }}</span>
          </span>
        </RouterLink>

        <div class="mt-2.5 flex gap-2">
          <button
            type="button"
            :disabled="answering === request.id"
            class="flex-1 rounded-full bg-primary-container px-3 py-1.5 text-label-sm text-on-primary transition-colors hover:bg-primary disabled:opacity-60"
            @click="answer(request.id, true)"
          >
            {{ t('friends.accept') }}
          </button>
          <button
            type="button"
            :disabled="answering === request.id"
            class="flex-1 rounded-full bg-surface-container-high px-3 py-1.5 text-label-sm text-on-surface transition-colors hover:bg-surface-container-highest disabled:opacity-60"
            @click="answer(request.id, false)"
          >
            {{ t('friends.reject') }}
          </button>
        </div>
      </div>

      <RouterLink
        v-if="users.incomingRequests.length > pending.length"
        to="/friends"
        class="mt-2 block text-label-sm text-primary hover:underline"
      >
        {{ t('nav.seeAllRequests') }}
      </RouterLink>
    </section>

    <section v-if="ready" class="rounded-xl bg-surface-container-lowest p-space-md shadow-sm">
      <div class="mb-3 flex items-center justify-between gap-2">
        <h2 class="text-headline-sm text-on-surface">{{ t('nav.contacts') }}</h2>
        <span v-if="users.friends.length" class="text-label-sm tabular-nums text-on-surface-variant">
          {{ users.friends.length }}
        </span>
      </div>

      <button
        v-for="friend in friends"
        :key="friend.id"
        type="button"
        :disabled="busyUserId === friend.id"
        class="-mx-2 flex w-[calc(100%+1rem)] items-center gap-3 rounded-xl px-2 py-2 text-left transition-colors hover:bg-surface-container-low disabled:opacity-60"
        @click="openWith(friend)"
      >
        <Avatar
          :name="friend.displayName"
          :src="friend.avatarUrl"
          :asset="friend.avatarAsset"
          :user-id="friend.id"
          size="sm"
          :online="presence.isOnline(friend.id)"
        />
        <span class="min-w-0 flex-1">
          <span class="block truncate text-label-md text-on-surface">{{ friend.displayName }}</span>
          <span class="block truncate text-label-sm text-outline">
            {{ countryFlag(friend.country) }} {{ countryName(friend.country, intlLocale) }}
          </span>
        </span>
      </button>

      <p v-if="friends.length === 0" class="py-2 text-body-sm text-on-surface-variant">
        {{ t('nav.noContacts') }}
        <RouterLink to="/discover" class="text-label-md text-primary hover:underline">
          {{ t('nav.findPeople') }}
        </RouterLink>
      </p>
    </section>

    <!-- Real suggestions, from the documented ranking — not a trending panel. -->
    <section
      v-if="ready && suggestions.length"
      class="rounded-xl bg-surface-container-lowest p-space-md shadow-sm"
    >
      <div class="mb-3 flex items-center justify-between gap-2">
        <h2 class="min-w-0 text-headline-sm text-on-surface">{{ t('nav.suggested') }}</h2>
        <RouterLink to="/discover" class="shrink-0 text-label-sm text-primary hover:underline">
          {{ t('nav.seeMore') }}
        </RouterLink>
      </div>

      <div class="space-y-3">
        <div v-for="person in suggestions" :key="person.id" class="flex items-center justify-between gap-2">
          <RouterLink :to="`/@${person.username}`" class="flex min-w-0 items-center gap-2.5">
            <Avatar
              :name="person.displayName"
              :src="person.avatarUrl"
              :asset="person.avatarAsset"
              :user-id="person.id"
              size="sm"
            />
            <span class="min-w-0">
              <span class="block truncate text-label-md text-on-surface hover:underline">
                {{ person.displayName }}
              </span>
              <!-- Where they are and what they read: what this app matches on. -->
              <span class="block truncate text-label-sm text-outline">
                {{ countryFlag(person.country) }} {{ languageName(person.language, intlLocale) }}
              </span>
            </span>
          </RouterLink>
          <button
            v-if="person.connection === 'none'"
            type="button"
            :disabled="connecting === person.id"
            class="shrink-0 rounded-full bg-surface-container-high px-3 py-1.5 text-label-sm text-on-surface transition-colors hover:bg-primary-container hover:text-on-primary disabled:opacity-60"
            @click="connect(person.id)"
          >
            {{ t('friends.connect') }}
          </button>
        </div>
      </div>
    </section>

    <nav class="flex flex-wrap items-center gap-x-3 gap-y-1 px-2 text-label-sm text-outline">
      <RouterLink to="/download" class="transition-colors hover:text-primary">
        {{ t('download.footerLink') }}
      </RouterLink>
      <RouterLink to="/terms" class="transition-colors hover:text-primary">
        {{ t('legal.terms') }}
      </RouterLink>
      <RouterLink to="/privacy" class="transition-colors hover:text-primary">
        {{ t('legal.privacy') }}
      </RouterLink>
      <span>© {{ new Date().getFullYear() }} TalkVibe</span>
    </nav>
  </aside>
</template>
